import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';

import {
    useDeleteSubscriptionMutation,
    useGetCapitalAccountsQuery,
    useGetCapitalCallsQuery,
    useLazyGetCurrentUserQuery,
    useGetClosingsQuery,
    useGetShareSubcategoriesQuery,
    useGetNAVQuery,
} from 'api/main';
import { userSelectors } from 'reducers';
import { subscriptionStatus, paymentMethods, listingActionButtonStyle } from 'utils/constants';
import { parseInvestmentFundName, parseSum, ucFirst } from 'utils/helpers';
import {
    useInvestorPortfolioData,
    useShareSubCategory,
    useSubscriptionFormSignatureStatus,
} from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import PageWrapper from './PageWrapper';
import { InfoCard } from 'containers/dashboard/common-components';
import {
    ActionButton,
    CancelButton,
    ConfirmDialog,
    DataTile,
    InfoDialog,
    Loader,
    SubmitButton,
} from 'components';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { SubscriptionInfo } from '.';

const infoCardProps = {
    disableGrid: true,
    width: '80%',
    flexBase: '40%',
};

const InvestmentRow = ({ signedDate, investmentFundName, sum, infoLabel }) => {
    const sumWithSign = sum === 0 ? parseSum(sum) : `-${parseSum(sum)}`;
    return (
        <TableRow>
            <TableCell sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        marginRight: '60px',
                        color: 'grey.700',
                    }}
                >
                    {signedDate ? signedDate.format('L') : ''}
                </Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        marginRight: '60px',
                        color: 'grey.700',
                    }}
                >
                    {infoLabel}
                </Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        marginRight: '60px',
                        color: 'common.black',
                    }}
                >
                    {investmentFundName}
                </Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        textAlign: 'right',
                        color: 'error.main',
                        '@media (max-width: 600px)': {
                            paddingRight: '20px',
                        },
                    }}
                >
                    {sumWithSign}
                </Typography>
            </TableCell>
        </TableRow>
    );
};

const InvestmentGroup = ({
    signedDate,
    investmentFundName,
    paymentMethod,
    entryFees,
    premiumAmount,
    totalAmount,
}) => {
    const signedDatePlus10 = moment(signedDate).add(10, 'days');
    const displayPremiumAmount = useMemo(
        () => premiumAmount !== null && !Number.isNaN(premiumAmount) && premiumAmount > 0,
        [premiumAmount]
    );
    return (
        <TableContainer
            sx={{
                overflow: 'auto',
                padding: '0 60px',
                '@media (max-width: 600px)': {
                    padding: '0 20px',
                },
            }}
        >
            <Table>
                <TableBody>
                    <InvestmentRow
                        signedDate={signedDate}
                        infoLabel={texts.ADMISSION_FEES}
                        investmentFundName={investmentFundName}
                        sum={entryFees}
                    />
                    {displayPremiumAmount && (
                        <InvestmentRow
                            signedDate={signedDate}
                            infoLabel={texts.SUBSCRIPTION_COMMISSION_VALUE}
                            investmentFundName={investmentFundName}
                            sum={premiumAmount}
                        />
                    )}
                    <InvestmentRow
                        signedDate={signedDatePlus10}
                        infoLabel={
                            paymentMethod === paymentMethods.DIRECT_DEBIT
                                ? texts.DIRECT_DEBIT
                                : texts.CREDIT_TRANSFER
                        }
                        investmentFundName={investmentFundName}
                        sum={totalAmount}
                    />
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const InvestorDashboard = () => {
    const navigate = useNavigate();

    const [disclaimerDialogOpen, setDisclaimerDialogOpen] = useState(false);
    const [cancelSubscriptionOpen, setCancelSubscriptionOpen] = useState(false);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription); // first finished subscription
    const lastFinishedSubscription = useSelector(userSelectors.getLastFinishedSubscription);
    const {
        investorProfile = {},
        impersonatedByUser,
        subscriptions = [],
        ...internalUser
    } = useSelector(userSelectors.getInternalUser);
    const { investingSum } = lastFinishedSubscription.investmentCharacteristics;

    const [getCurrentUser] = useLazyGetCurrentUserQuery();
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery();
    const [deleteSubscription, { isSuccess: deleteSuccessful, isLoading: deleteLoader }] =
        useDeleteSubscriptionMutation();
    const { data: capitalCalls = [], isLoading: capitalCallsLoader } = useGetCapitalCallsQuery({
        status: ['completed', 'activated'],
    });
    const { data: capitalAccounts = [], isLoading: capitalAccountsLoader } =
        useGetCapitalAccountsQuery({});
    const { data: closings = [], isLoading: closingsLoader } = useGetClosingsQuery({
        status: ['completed', 'activated'],
    });

    const { data: navs = [] } = useGetNAVQuery({
        pagination: false,
        status: ['completed', 'activated'],
    });

    const finishedSubscriptions = useMemo(
        () =>
            subscriptions.filter(
                s =>
                    s.subscriptionStatus === subscriptionStatus.FINISHED && s.investmentFund.visible
            ),
        [subscriptions]
    );

    const totalInvestedSum = useMemo(
        () =>
            finishedSubscriptions.reduce(
                (acc, crt) => acc + crt.investmentCharacteristics.investingSum,
                0
            ),
        [finishedSubscriptions]
    );
    const coSubscriber = useMemo(
        () =>
            subscriptions.find(s => s.subscriptionStatus === subscriptionStatus.FINISHED)
                ?.coSubscriber,
        [subscriptions]
    );
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const hasInProgressSubscription = useSelector(userSelectors.getInProgressSubscriptionStatus);
    const hasSignedBS = useSubscriptionFormSignatureStatus(tunnelSubscription);

    const subscriptionFinished = useMemo(
        () => currentSubscription.subscriptionStatus === subscriptionStatus.FINISHED,
        [currentSubscription]
    );

    const investmentFundName = useMemo(
        () => parseInvestmentFundName(currentSubscription.investmentFund),
        [currentSubscription]
    );

    const signedByServicerDate = currentSubscription.finishedSubscriptionAt
        ? moment(currentSubscription.finishedSubscriptionAt)
        : null;

    const shareSubCategory = useShareSubCategory();
    const paymentMethod = currentSubscription?.bankingStatement?.paymentMethod;
    const admissionFee = currentSubscription?.investmentCharacteristics?.admissionFee;

    const premiumAmount = useMemo(
        () => (investingSum * (shareSubCategory?.subscriptionFee || 0)) / 100,
        [shareSubCategory, investingSum]
    );

    const entryFees = useMemo(
        () => (investingSum * admissionFee) / 100,
        [admissionFee, investingSum]
    );

    const totalAmount = investingSum + premiumAmount + entryFees;
    const atLeastOneFinishedDirectSubscription = useMemo(
        () => !!subscriptions.find(s => !s.createdBy),
        [subscriptions]
    );

    const hasOnlyDirectSubscriptions = useMemo(
        () => subscriptions.length > 0 && subscriptions.every(s => !s.distributorCompany),
        [subscriptions]
    );

    const handleNewSubscription = useCallback(() => navigate('/new-subscription'), [navigate]);

    const totalData = useInvestorPortfolioData({
        finishedSubscriptions,
        capitalAccounts,
        shareSubcategories,
        capitalCalls,
        closings,
        navs,
    });

    const handleOpenCancelSubscription = useCallback(() => setCancelSubscriptionOpen(true), []);
    const handleCloseCancelSubscription = useCallback(() => setCancelSubscriptionOpen(false), []);

    const handleCancelSubscription = useCallback(() => {
        deleteSubscription({ id: tunnelSubscription.id, refetchMe: true });
    }, [deleteSubscription, tunnelSubscription]);

    useEffect(() => {
        if (deleteSuccessful) {
            setCancelSubscriptionOpen(false);
            getCurrentUser();
        }
    }, [deleteSuccessful, getCurrentUser]);

    return (
        <PageWrapper
            title={
                <React.Fragment>
                    {`${texts.BONJOUR} ${ucFirst(internalUser?.firstName)}, ${
                        coSubscriber ? ` ${texts.BONJOUR} ${ucFirst(coSubscriber.firstName)},` : ''
                    } `}
                    <br></br>
                    {texts.BIENVENUE_ESPACE_PERSONNEL}
                </React.Fragment>
            }
        >
            {subscriptionFinished ? (
                <Loader
                    loading={
                        capitalAccountsLoader ||
                        subcategoriesLoader ||
                        capitalCallsLoader ||
                        closingsLoader
                    }
                >
                    {hasOnlyDirectSubscriptions && (
                        <Box display="flex" justifyContent="center" marginBottom={4}>
                            <Tooltip title={texts.NEW_SUBSCRIPTION_TOOLTIP}>
                                <span>
                                    <ActionButton
                                        variant="outlined"
                                        sx={listingActionButtonStyle}
                                        onClick={handleNewSubscription}
                                    >
                                        {texts.NEW_SUBSCRIPTION}
                                    </ActionButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap="16px"
                        mb={4}
                    >
                        <InfoCard
                            number={finishedSubscriptions.length}
                            label={texts.SUBSCRIPTIONS.toUpperCase()}
                            {...infoCardProps}
                        />
                        <InfoCard
                            number={parseSum(totalInvestedSum)}
                            label={texts.TOTAL_ENGAGEMENTS}
                            {...infoCardProps}
                        />
                        <InfoCard
                            number={parseSum(totalData.totalCapitalCalled)}
                            label={texts.CAPITAL_CALLED_TO_DATE}
                            {...infoCardProps}
                        />
                        <InfoCard
                            number={parseSum(totalData.totalValueToDate)}
                            label={texts.LATEST_VALORIZATION}
                            {...infoCardProps}
                        />
                    </Box>
                    {signedByServicerDate && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={1}
                            paddingY={2.5}
                            alignItems="center"
                        >
                            <Typography
                                variant="body2"
                                align="left"
                                gutterBottom
                                sx={{
                                    fontSize: '13px',
                                    letterSpacing: '1.3px',
                                    lineHeight: '16px',
                                    color: 'grey.700',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {texts.MEMBER_SINCE}
                            </Typography>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontSize: '22px', color: 'common.black' }}
                            >
                                {signedByServicerDate.format('LL')}
                            </Typography>
                        </Box>
                    )}
                    {/* // TODO: uncomment this when we get the green light */}
                    {false && (
                        <DataTile
                            title={texts.HISTORIQUE_INVESTISSEMENTS}
                            sx={{
                                padding: 0,
                                flexDirection: 'column',
                            }}
                        >
                            <InvestmentGroup
                                signedDate={signedByServicerDate}
                                investmentFundName={investmentFundName}
                                paymentMethod={paymentMethod}
                                entryFees={entryFees}
                                premiumAmount={premiumAmount}
                                totalAmount={totalAmount}
                            />
                        </DataTile>
                    )}
                </Loader>
            ) : (
                <DataTile sx={{ flexDirection: 'column' }}>
                    <SubscriptionInfo subscription={currentSubscription} />
                </DataTile>
            )}
            {!investorProfile?.usPerson &&
                !hasSignedBS &&
                (!tunnelSubscription?.indirectUntransferred || !!impersonatedByUser) && ( // in progress subscription is not transferred
                    <Box textAlign="center" mb={6}>
                        {hasInProgressSubscription && (
                            <SubmitButton
                                text={texts.CONTINUE_YOUR_SUBSCRIPTION}
                                onClick={() => navigate('/new-subscription')}
                            />
                        )}
                        {hasInProgressSubscription && (
                            <Typography
                                sx={{ marginTop: 1, textTransform: 'uppercase', fontSize: '14px' }}
                            >
                                {texts.ON_FUND(tunnelSubscription.investmentFund.name)}
                            </Typography>
                        )}
                        {hasInProgressSubscription && atLeastOneFinishedDirectSubscription && (
                            <Box marginY="20px">
                                <CancelButton
                                    variant="contained"
                                    onClick={handleOpenCancelSubscription}
                                >
                                    {texts.CANCEL_ONGOING_SUBSCRIPTION}
                                </CancelButton>
                            </Box>
                        )}
                    </Box>
                )}
            <ConfirmDialog
                open={cancelSubscriptionOpen}
                loading={deleteLoader}
                title={texts.CANCEL_SUBSCRIPTION_TITLE}
                confirmText={texts.CONFIRMER}
                cancelText={texts.CANCEL}
                handleClose={handleCloseCancelSubscription}
                handleConfirm={handleCancelSubscription}
            >
                <Typography variant="body2">{texts.CONFIRM_CANCEL_SUBSCRIPTION}</Typography>
            </ConfirmDialog>
            <InfoDialog
                open={disclaimerDialogOpen}
                title={texts.SUBSCRIPTION}
                message={texts.INDIRECT_UBSCRIPTION_DISCLAIMER}
                handleClose={() => setDisclaimerDialogOpen(false)}
            />
        </PageWrapper>
    );
};

export default InvestorDashboard;
